import classNames from 'classnames';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import Box, { BoxProps } from 'ui-box';
import { Intents, minorScale } from '.';
import { Tag } from './Tag';
import { useCombinedRefs } from './useCombineRefs';

export interface ITagInputProps extends BoxProps<any> {
  value?: string[];
  defaultValue?: string[];
  onChange?: (tags: string[]) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement>) => void;
  tagColor?: Intents;
  isInvalid?: boolean;
  validationMessage?: string;
}

export const TagInput: React.FC<ITagInputProps> = forwardRef<
  HTMLInputElement,
  ITagInputProps
>(
  (
    {
      placeholder = 'Tilføj tags',
      value,
      defaultValue = [],
      onChange,
      onBlur,
      onFocus,
      tagIntent = 'info',
      isInvalid = false,
      validationMessage = '',
      ...props
    },
    ref
  ) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const combinedRef = useCombinedRefs<HTMLInputElement>(inputRef, ref);
    const [tags, setTags] = useState<string[]>(defaultValue);
    const [focussed, setFocussed] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const [prevInputValue, setPrevInputValue] = useState('');

    useEffect(() => {
      if (value) setTags(value);
    }, [value]);

    const addTag = (tag: string) => {
      if (tags.indexOf(tag) > -1) return;
      const tts = tags.concat([tag.trim()]);
      setTags(tts);
      onChange && onChange(tts);
    };

    const removeTag = (index?: number) => {
      if (tags.length === 0) return;
      const newTags = Array.from(tags);
      if (index === undefined) {
        newTags.splice(tags.length - 1, 1);
        setTags(newTags);
      } else {
        newTags.splice(index, 1);
        setTags(newTags);
      }
      onChange && onChange(newTags);
    };

    return (
      <>
        <Box
          {...props}
          className={classNames(
            'input-group',
            focussed && 'focus',
            isInvalid && 'invalid'
          )}
          backgroundColor="white"
          cursor="text"
          onClick={() => inputRef.current && inputRef.current.focus()}
        >
          {tags.map((tag, i) => (
            <Tag
              key={tag + i}
              intent={tagIntent}
              marginRight={minorScale(1)}
              marginBottom={minorScale(1) / 2}
              small={true}
              onRemove={() => removeTag(i)}
            >
              {tag}
            </Tag>
          ))}
          <input
            type="text"
            ref={combinedRef}
            size={16}
            style={{ minWidth: '80px', padding: 0, margin: 0 }}
            className="input-group minimal"
            placeholder={placeholder}
            value={inputValue}
            onChange={(event) => {
              setInputValue(event.target.value);
              setPrevInputValue(inputValue);
            }}
            onFocus={(e) => {
              setFocussed(true);
              onFocus && onFocus(e);
            }}
            onBlur={(e) => {
              setFocussed(false);
              onBlur && onBlur(e);
              if (inputValue) {
                addTag(inputValue);
                setInputValue('');
                setPrevInputValue('');
              }
            }}
            onKeyUp={(event: React.KeyboardEvent<HTMLInputElement>) => {
              const target = event.target as HTMLInputElement;
              if (inputValue === '') {
                setPrevInputValue('');
              }
              if (
                (event.key === 'Enter' || event.key === ',') &&
                target.value.trim() !== ''
              ) {
                addTag(target.value.replace(',', ''));
                setInputValue('');
                setPrevInputValue('');
              } else if (event.key === 'Backspace' && prevInputValue === '') {
                removeTag();
              }
            }}
          />
        </Box>
        {validationMessage && (
          <Box
            marginLeft={minorScale(1)}
            className={classNames('p small muted', isInvalid && 'danger')}
            marginBottom={minorScale(1)}
          >
            {validationMessage}
          </Box>
        )}
      </>
    );
  }
);
